<script setup lang="ts">
/**
 * 画像編集画面の「編集」: ペイントツール(miniPaint)で画像を手で直す。
 *
 * miniPaint は public/minipaint/ に置いた自前のコピー(日本語に直したもの)を iframe で開く。
 * 同じドメインなので、iframe の中の miniPaint(Layers / FileOpen)を直接呼べる。
 *   開く  : 画像を読み込み、キャンバスを画像の大きさにする
 *   保存  : 全レイヤーを1枚に描き、一時置き場へアップロードして親(画像編集画面)へ渡す
 * 親は「登録する画像」のその画像を、保存した画像に差し替える(登録するまで商品画像は変わらない)。
 */
import { ref, onBeforeUnmount } from 'vue'
import * as api from '@/api/endpoints'
import { useConfirmModalStore } from '@/stores/confirmModal'

const props = defineProps<{
  /** 編集する画像のURL */
  url: string
  /** 画面に出す名前(「現在 #1」など) */
  label: string
}>()
const emit = defineEmits<{
  close: []
  /** 保存した画像(一時置き場のキーと、表示用のURL) */
  saved: [payload: { stagingKey: string; url: string; contentType: string; width: number; height: number }]
}>()

const confirmModal = useConfirmModalStore()
const frame = ref<HTMLIFrameElement | null>(null)
const state = ref<'loading' | 'ready' | 'saving' | 'error'>('loading')
const errorText = ref('')
/** 元の画像の種類(PNGならPNGのまま、それ以外はJPEGで保存する) */
let sourceType = 'image/jpeg'
let objectUrl: string | null = null

interface MiniPaintWindow extends Window {
  Layers?: {
    reset_layers: (autoInsert: boolean) => Promise<unknown>
    insert: (settings: Record<string, unknown>, canAutomate?: boolean) => Promise<unknown>
    autoresize: (w: number, h: number, layerId?: number | null, canAutomate?: boolean) => Promise<unknown>
    get_dimensions: () => { width: number; height: number }
    convert_layers_to_canvas: (ctx: CanvasRenderingContext2D, layerId?: number | null, isPreview?: boolean) => void
  }
}

function win(): MiniPaintWindow | null {
  return (frame.value?.contentWindow as MiniPaintWindow | null) ?? null
}

/** miniPaint の起動を待つ(load の後に初期化されるため) */
async function waitReady(timeoutMs = 15000) {
  const t0 = Date.now()
  while (Date.now() - t0 < timeoutMs) {
    if (win()?.Layers) return
    await new Promise((r) => setTimeout(r, 100))
  }
  throw new Error('ペイントツールを起動できませんでした。')
}

async function loadImage() {
  try {
    await waitReady()
    const res = await fetch(props.url, { cache: 'no-store' })
    if (!res.ok) throw new Error(`画像を読み込めませんでした (HTTP ${res.status})`)
    const blob = await res.blob()
    sourceType = blob.type === 'image/png' ? 'image/png' : 'image/jpeg'
    objectUrl = URL.createObjectURL(blob)
    const w = win()!
    const img = new (w as unknown as typeof globalThis).Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('画像を読み込めませんでした。'))
      img.src = objectUrl!
    })
    const L = w.Layers!
    // 最初の空のレイヤーを消してから画像を入れ、キャンバスを画像の大きさにする
    await L.reset_layers(false)
    await L.insert({
      name: props.label,
      type: 'image',
      link: img,
      x: 0,
      y: 0,
      width: img.naturalWidth,
      height: img.naturalHeight,
      width_original: img.naturalWidth,
      height_original: img.naturalHeight,
    })
    await L.autoresize(img.naturalWidth, img.naturalHeight, null, true)
    state.value = 'ready'
  } catch (e) {
    errorText.value = (e as Error).message
    state.value = 'error'
  }
}

async function save() {
  const w = win()
  if (!w?.Layers || state.value !== 'ready') return
  state.value = 'saving'
  try {
    const dim = w.Layers.get_dimensions()
    const canvas = document.createElement('canvas')
    canvas.width = dim.width
    canvas.height = dim.height
    const ctx = canvas.getContext('2d')!
    if (sourceType === 'image/jpeg') {
      // JPEG は透明を持てないので、透明な所は白にする(商品画像は白背景)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, dim.width, dim.height)
    }
    w.Layers.convert_layers_to_canvas(ctx, null, false)
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('画像を作れませんでした。'))), sourceType, 0.95),
    )
    const { stagingKey, contentType } = await api.uploadToStaging(blob, sourceType)
    emit('saved', { stagingKey, url: URL.createObjectURL(blob), contentType, width: dim.width, height: dim.height })
  } catch (e) {
    errorText.value = `保存できませんでした: ${(e as Error).message}`
    state.value = 'ready'
  }
}

async function cancel() {
  if (state.value === 'saving') return
  if (state.value === 'ready' && !(await confirmModal.ask('編集した内容は保存されません。閉じますか？'))) return
  emit('close')
}

let started = false
function onFrameLoad() {
  if (started) return
  started = true
  loadImage()
}
onBeforeUnmount(() => {
  if (objectUrl) URL.revokeObjectURL(objectUrl)
})
</script>

<template>
  <div class="pe-overlay">
    <div class="pe-modal" role="dialog" aria-modal="true" aria-label="画像を編集">
      <div class="pe-head">
        <h2>画像を編集</h2>
        <span class="pe-crumb">{{ label }}</span>
        <span class="pe-hint">保存すると「登録する画像」のこの画像が差し替わります（登録するまで商品画像は変わりません）</span>
        <span class="pe-grow"></span>
        <span v-if="errorText && state !== 'error'" class="pe-error">{{ errorText }}</span>
        <button class="btn btn-ghost" :disabled="state === 'saving'" @click="cancel">キャンセル</button>
        <button class="btn btn-primary" :disabled="state !== 'ready'" @click="save">
          <span v-if="state === 'saving'" class="spinner spinner-sm"></span>
          {{ state === 'saving' ? '保存中…' : '保存して差し替え' }}
        </button>
      </div>
      <div class="pe-body">
        <iframe ref="frame" class="pe-frame" src="/minipaint/index.html?lang=ja" title="ペイントツール" @load="onFrameLoad"></iframe>
        <div v-if="state === 'loading'" class="pe-cover"><span class="spinner"></span> ペイントツールを開いています…</div>
        <div v-else-if="state === 'error'" class="pe-cover pe-cover-error">
          <p>{{ errorText }}</p>
          <button class="btn btn-ghost" @click="emit('close')">閉じる</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pe-overlay{position:fixed;inset:0;z-index:1080;background:rgba(10,14,20,.6);display:flex;padding:8px;}
.pe-modal{flex:1;display:flex;flex-direction:column;background:var(--panel);border-radius:12px;overflow:hidden;box-shadow:var(--shadow-md);}
.pe-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:6px 14px;border-bottom:1px solid var(--line);}
.pe-head h2{margin:0;font-size:16px;font-weight:700;color:var(--brand-dark);}
.pe-crumb{font-size:13px;color:var(--ink-soft);}
.pe-hint{font-size:11.5px;color:var(--ink-faint);}
.pe-grow{flex:1 1 auto;}
.pe-error{font-size:12px;font-weight:700;color:var(--ng);}
.pe-head .btn{min-width:110px;padding:7px 16px;font-size:13px;}
.pe-body{position:relative;flex:1;min-height:0;}
.pe-frame{width:100%;height:100%;border:0;display:block;}
.pe-cover{
  position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;
  background:var(--panel);font-size:14px;color:var(--ink-soft);
}
.pe-cover-error{color:var(--ng);}
</style>
